<template>
  <div
    class="w-80 bg-white dark:bg-navy-900 border border-gray-300 dark:border-gray-800 border-b-0 shadow-2xl rounded-t-xl overflow-hidden flex flex-col pointer-events-auto transition-all duration-200"
    :class="[
      isMinimized ? 'h-12' : 'h-[420px]'
    ]"
  >
    <div
      class="h-12 text-white px-3 flex items-center justify-between cursor-pointer select-none"
      :class="isFocused ? 'bg-primary-400 dark:bg-navy-800' : 'bg-primary-300/90 dark:bg-navy-800'"
      @click="toggleMinimize"
    >
      <div class="flex items-center space-x-2 min-w-0">
        <span class="font-medium text-sm truncate">{{ partnerName }}</span>
        <span v-if="partnerIsTyping" class="text-xs text-white">(typing...)</span>
      </div>
      
      <div class="flex items-center space-x-2" @click.stop>
        <button @click="toggleMinimize" class="hover:bg-primary-600 dark:hover:bg-gray-700 p-1 rounded transition-colors">
          <svg v-if="isMinimized" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
        <button @click="$emit('maximize', partnerId)" class="hover:bg-primary-600 dark:hover:bg-gray-700 p-1 rounded transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" /></svg>
        </button>
        <button @click="$emit('close', partnerId)" class="hover:bg-primary-600 dark:hover:bg-gray-700 p-1 rounded transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    </div>

    <div
      v-show="!isMinimized"
      class="flex-1 flex flex-col min-h-0 "
      :class="isFocused ? 'bg-gray-100 dark:bg-navy-900' : 'bg-gray-50 dark:bg-navy-1000'"
    >
      <MessageChatBody
        :key="partnerId"
        :display-messages="displayedMessages"
        :is-system-convo="false"
        :loading="loading"
        :loading-more="false"
        :has-more-messages="displayedMessages.length >= 50"
        :partner-is-typing="partnerIsTyping"
        :current-user-id="currentUserId"
        :dismissed-ids="[]"
        :is-mini="true"
        :unfocused="!isFocused"
        @load-more="loadMoreHistory"
        @retry-send="handleRetry"
      />
    </div>

    <div v-show="!isMinimized" class="p-2 border-t border-gray-100 dark:border-navy-800 bg-white dark:bg-navy-900 rounded-b-xl">
      <form @submit.prevent="sendMessage" class="flex items-center space-x-2">
        <input
          v-model="messageText"
          type="text"
          @keydown="handleInputKeydown"
          placeholder="Type a message..."
          class="mini-chat-input flex-1 min-w-0 px-3 py-1.5 text-sm bg-gray-100 dark:bg-navy-800 border border-transparent focus:border-blue-500 rounded-lg outline-none text-gray-800 dark:text-gray-100"
        />
        <button 
          type="submit" 
          :disabled="!messageText.trim()"
          class="p-1.5 bg-secondary-400 dark:bg-navy-500 text-white rounded-lg hover:bg-secondary-600 dark:hover:bg-primarydark disabled:opacity-40 disabled:hover:bg-secondary-400 disabled:dark:hover:bg-navy-500 transition-colors"
        >
          <svg class="w-4 h-4 transform rotate-90" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MessageChatBody from '@/views/Messages/MessageChatBody.vue';
import { getMessages, markMessagesRead } from '@/services/message.service';
import { emitTyping, emitReadReceipt } from '@/services/socket.service'

export default {
  name: 'MiniChatWindow',
  components: {
    MessageChatBody
  },
  props: {
    partnerId: {
      type: String,
      required: true
    },
    currentUserId: {
      type: String,
      required: true
    }
  },
  emits: [ "maximize", "close"],
  data() {
    return {
      messageText: '',
      isMinimized: false,
      isFocused: false,
      localMessages: [], // Isolated data silo for this specific window instance
      historicalMessages: [], // Back-filled database history (infinite scroll chunk)
      storeMessages: [],      // Pure sequential mirror of active store/socket activities
      loading: false,
      loadingMore: false,
      hasMoreMessages: true
    };
  },
  computed: {
    ...mapGetters([
      "getCombinedMessages",
      "getChatConversations",
      "getChatTyping"
    ]),
    partnerName() {
      const convo = this.getChatConversations?.find(c => String(c.id) === String(this.partnerId));
      return convo ? convo.name || convo.first_name : 'Chat';
    },
    partnerIsTyping() {
      const typingMap = this.getChatTyping
      // console.log(typingMap)
      // console.log(this.partnerId)
      const timestamp = typingMap[this.partnerId]
      // console.log(Date.now() - timestamp)
      // console.log(timestamp && (Date.now() - timestamp) < 3000)
      if (!timestamp) return false
      return timestamp && (Date.now() - timestamp) < 3000
    },
    /**
     * UNIFIED DISPLAY PIPELINE
     * Merges historical records with live store arrays. Uses a JavaScript Map
     * to preserve exact insertion/chronological sequence while deduplicating boundaries.
     */
    displayedMessages() {
      const uniqueMap = new Map();

      // 1. Append older historical blocks
      this.historicalMessages.forEach(msg => {
        if (msg.id) uniqueMap.set(String(msg.id), msg);
      });

      // 2. Overwrite or append live tracking strings
      this.storeMessages.forEach(msg => {
        const key = msg.id ? String(msg.id) : msg._localKey;
        uniqueMap.set(key, msg);
      });

      return Array.from(uniqueMap.values());
    },
  },
  watch: {
    /**
     * If the window is recycled or the partner ID mutates, hot-reload the data stream
     */
    partnerId: {
      immediate: true,
      handler() {
        this.historicalMessages = [];
        this.storeMessages = [];
        this.localMessages = [];
        this.hasMoreMessages = true;
        this.syncAndFetchMessages();
      }
    },
    /**
     * Listen to global store updates. If a new message arrives via WebSockets 
     * while the mini-window is open, append it instantly to our local view.
     */
    getCombinedMessages: {
      deep: true,
      handler(newGlobalMessages) {
        // console.log("new global messages", newGlobalMessages)
        if (!newGlobalMessages || newGlobalMessages.length === 0) return;
        // debugger

        // Filter messages belonging to this specific room thread
        const relevant = newGlobalMessages.filter(msg => {
          const roomId = msg.recipient_id;
          return String(roomId) === String(this.partnerId) || 
                 String(msg.sender_id) === String(this.partnerId);
        });
        // console.log("relevant:", relevant)
        // debugger

        // CRITICAL ROUTE GUARD: If MessagesPage unmounts and wipes the store,
        // relevant.length will hit 0. We block this update to guard our cache snapshot!
        if (relevant.length === 0) return;

        // BIND STRUCTURAL POSITION: Map items by sequential position index.
        // This makes spamming identical messages completely immune to matching collisions.
        this.storeMessages = relevant.map((msg, index) => ({
          ...msg,
          created_at: msg.created_at || new Date().toISOString(),
          // Positional fallback avoids duplicate keys before database assignment
          _localKey: msg.id ? String(msg.id) : `pending-${index}-${msg.content}`
        }));
      },
    },
    // 3. Watch structural visibility & focus states to trigger receipts
    isMinimized(val) {
      if (!val) this.maybeMarkAsRead();
    },
    isFocused(val) {
      if (val) this.maybeMarkAsRead();
    },
    displayedMessages: {
      deep: true,
      handler() {
        this.maybeMarkAsRead();
      }
    }
  },
  mounted() {
    // console.log("mini chat mounted")
    this.syncAndFetchMessages()
    // Attach window and click listeners to monitor focus transitions
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('focus', this.handleWindowFocusChange);
    window.addEventListener('blur', this.handleWindowFocusChange);
    
    // Check initial focus if window mounted in active tab
    if (document.hasFocus()) {
      this.isFocused = true;
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('focus', this.handleWindowFocusChange);
    window.removeEventListener('blur', this.handleWindowFocusChange);
  },
  methods: {
    ...mapActions(['executeCentralizedSend']),

    /**
     * Pulls historical message packets directly into this component instance
     */
    async syncAndFetchMessages() {
      if (!this.partnerId || !this.currentUserId) return;

      // 1. ZERO-LATENCY HYDRATION: Check the store immediately before it can be altered
      const globalMessages = this.getCombinedMessages || [];
      // console.log(this.getCombinedMessages)
      const relevant = globalMessages.filter(msg => {
        // const roomId = msg.recipient_id;
        return String(msg.recipient_id) === String(this.partnerId) || String(msg.sender_id) === String(this.partnerId);
      });
      // console.log("relevant:", relevant)

      if (relevant.length > 0) {
        // console.log("already have messages!")
        // Create an isolated snapshot copy so global store wipes can't touch it
        this.storeMessages = relevant.map(msg => ({
          ...msg,
          created_at: msg.created_at || new Date().toISOString(),
          _localKey: msg.id ? String(msg.id) : `pending-${index}-${msg.content}`
        }));
        this.loading = false;
        return; // Exit early, skipping network spinning layout states entirely
      }

      // 2. NETWORK FALLBACK: Triggered only if opened from clean pages (e.g., Dashboard)
      this.loading = true;
      try {
        // console.log("need to load messages")
        const response = await getMessages(this.currentUserId, this.partnerId, 30, 0);
        if (response?.status === 200 && response.data) {
          this.historicalMessages = [...response.data].reverse();
          if (response.data.length < 30) this.hasMoreMessages = false;
        }
      } catch (e) {
        console.error(`[MiniChat] History compilation failure:`, e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Hand off message execution entirely to global Vuex architecture
     */
    async sendMessage() {
      const cleanText = this.messageText.trim();
      if (!cleanText) return;

      this.messageText = '';

      // console.log("partnerId", this.partnerId)
      // Instantly broadcast that we have stopped typing
      emitTyping(this.partnerId, false);

      try {
        // Triggers the store action, which appends the text to combinedMessages.
        // Our getCombinedMessages watcher above will automatically catch it.
        await this.executeCentralizedSend({
          convoId: this.partnerId,
          content: cleanText
        });
      } catch (err) {
        console.error('[MiniChat] Sending transmission error:', err);
      }
    },

    async loadMoreHistory() {
      if (this.loadingMore || !this.hasMoreMessages) return;
      
      this.loadingMore = true;
      try {
        const currentOffset = this.displayMessages.length;
        const response = await getMessages(this.currentUserId, this.partnerId, 30, currentOffset);
        
        if (response?.status === 200 && response.data?.length > 0) {
          const olderMessages = [...response.data].reverse();
          this.historicalMessages = [...olderMessages, ...this.historicalMessages];
          if (response.data.length < 30) this.hasMoreMessages = false;
        } else {
          this.hasMoreMessages = false;
        }
      } catch (e) {
        console.error('[MiniChat] Dynamic loading error:', e);
      } finally {
        this.loadingMore = false;
      }
    },

    handleRetry(failedMsg) {
      // Remove failed item instance from local trace view and re-process
      this.storeMessages = this.storeMessages.filter(m => m.tempId !== failedMsg.tempId);
      this.sendMessage();
    },
    handleInputKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        // e.preventDefault()
        // this.submitNewMessage()
        return
      }
      // console.log("typing...")
      emitTyping(this.partnerId)
    },
    /**
     * Determines if read-receipt triggers are met, updates backend/Vuex, 
     * and broadcasts a real-time read receipt to the partner.
     */
    async maybeMarkAsRead() {
      // Rule 1: Must be expanded
      if (this.isMinimized) return;
      // Rule 2: Must be in focus on the page
      if (!this.isFocused) return;

      // Find any unread message sent by the partner to the current user
      const hasUnread = this.displayedMessages.some(
        msg => msg.sender_id === this.partnerId && !msg.is_read
      );

      if (hasUnread) {
        try {
          console.log('[MiniChat] Unread messages detected. Marking read...');
          
          // 1. Update backend via REST
          await markMessagesRead(this.currentUserId, this.partnerId);
          
          // 2. Clear unread badge in Vuex state
          this.$store.commit('markConversationRead', this.partnerId);
          
          // 3. Inform the partner in real-time
          emitReadReceipt(this.partnerId);

          // 4. Update internal message caches immediately for smooth UI feedback
          this.storeMessages.forEach(msg => {
            if (msg.sender_id === this.partnerId && !msg.is_read) {
              msg.is_read = true;
            }
          });
          this.historicalMessages.forEach(msg => {
            if (msg.sender_id === this.partnerId && !msg.is_read) {
              msg.is_read = true;
            }
          });
        } catch (e) {
          console.error('[MiniChat] Failed to process read receipt:', e);
        }
      }
    },

    /**
     * Tracks click containment to identify if this specific component is focused.
     */
    handleOutsideClick(event) {
      if (this.$el && this.$el.contains(event.target)) {
        this.isFocused = true;
      } else {
        this.isFocused = false;
      }
    },

    /**
     * Integrates browser-tab level shifts into the focus evaluation.
     */
    handleWindowFocusChange() {
      if (!document.hasFocus()) {
        this.isFocused = false;
      }
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      if (!this.isMinimized) {
        this.isFocused = true; // Focus instantly when expanded
      }
    },
  }
};
</script>

<style scoped>
@media (pointer: coarse) {
  .mini-chat-input {
    font-size: 16px !important;
  }
}
</style>